import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { isInIframe } from '../utils/authUtils';
import { protectedResources } from '../authConfig';
import {
  FRAMED_AUTH_REQUEST,
  FRAMED_AUTH_TOKEN,
  FramedAuthAccount,
  FramedAuthRequestMessage,
  FramedAuthTokenMessage,
  isAllowedParentOrigin,
} from './framedAuthContract';

// The parent frame must mint/exchange a token for this scope, or the resulting
// accessToken's `aud` claim won't match this app's client ID and every API call will 401.
const REQUIRED_SCOPES = protectedResources.apiCFSkillPath.scopes.read;

// Guards against a runaway loop: every 401 triggers requestNewToken(), which (once the
// parent replies) re-renders every consumer and re-fires their fetch effects. If the parent
// keeps handing back a token the API rejects, that becomes an unbounded request storm with
// no user interaction to stop it (unlike the MSAL popup/redirect path). Trip the breaker after
// a burst of rapid attempts instead of hammering the API forever.
const MAX_RAPID_REQUEST_ATTEMPTS = 3;
const RAPID_REQUEST_WINDOW_MS = 5000;

type FramedAuthState = {
  accessToken: string;
  account: FramedAuthAccount;
  expiresOn: number;
};

type FramedAuthValue = (FramedAuthState & { requestNewToken: () => void }) | null;

const isFramedAuthTokenMessage = (data: any): data is FramedAuthTokenMessage =>
  !!data &&
  data.type === FRAMED_AUTH_TOKEN &&
  typeof data.accessToken === 'string' &&
  typeof data.expiresOn === 'number' &&
  !!data.account &&
  typeof data.account.username === 'string';

export const FramedAuthContext = React.createContext<FramedAuthValue>(null);

export const FramedAuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [token, setToken] = useState<FramedAuthState | null>(null);
  const rapidAttemptsRef = useRef(0);
  const lastAttemptAtRef = useRef(0);
  const breakerTrippedRef = useRef(false);

  const requestNewToken = useCallback(() => {
    if (breakerTrippedRef.current) {
      return;
    }

    const now = Date.now();
    rapidAttemptsRef.current = now - lastAttemptAtRef.current < RAPID_REQUEST_WINDOW_MS ? rapidAttemptsRef.current + 1 : 1;
    lastAttemptAtRef.current = now;

    if (rapidAttemptsRef.current > MAX_RAPID_REQUEST_ATTEMPTS) {
      breakerTrippedRef.current = true;
      console.error(
        '[FramedAuth] giving up: parent kept returning a token the API rejected after',
        MAX_RAPID_REQUEST_ATTEMPTS,
        'rapid attempts. Reload the page once the parent/auth configuration is fixed.'
      );
      return;
    }

    const message: FramedAuthRequestMessage = { type: FRAMED_AUTH_REQUEST, scopes: REQUIRED_SCOPES };
    console.info('[FramedAuth] posting auth request to parent', message, `(attempt ${rapidAttemptsRef.current}/${MAX_RAPID_REQUEST_ATTEMPTS})`);
    window.parent.postMessage(message, '*');
  }, []);

  useEffect(() => {
    const framed = isInIframe();
    console.info('[FramedAuth] provider mounted', { isInIframe: framed });
    if (!framed) return;

    const handleMessage = (event: MessageEvent) => {
      console.debug('[FramedAuth] message event received', { origin: event.origin, data: event.data });
      if (!isAllowedParentOrigin(event.origin)) {
        console.warn('[FramedAuth] ignoring message from disallowed origin', event.origin, event.data);
        return;
      }
      if (!isFramedAuthTokenMessage(event.data)) {
        console.warn('[FramedAuth] ignoring message with unexpected shape', event.data);
        return;
      }

      const { accessToken, account, expiresOn } = event.data;
      if (!accessToken) {
        console.warn('[FramedAuth] received empty accessToken', event.data);
      }
      if (!expiresOn) {
        console.warn('[FramedAuth] received empty/zero expiresOn', event.data);
      }
      if (!account.username) {
        console.warn('[FramedAuth] received empty account.username', event.data);
      }

      setToken({ accessToken, account, expiresOn });
    };

    window.addEventListener('message', handleMessage);
    requestNewToken();

    return () => window.removeEventListener('message', handleMessage);
  }, [requestNewToken]);

  const value: FramedAuthValue = useMemo(
    () => (token ? { ...token, requestNewToken } : null),
    [token, requestNewToken]
  );

  return <FramedAuthContext.Provider value={value}>{children}</FramedAuthContext.Provider>;
};

export const useFramedAuth = () => useContext(FramedAuthContext);
